import { useMemo, Fragment, useRef } from 'react';
import { Menu, Transition } from '@headlessui/react';
import Icon from '../Icons/Icon';
import Item from './Item';
import { classNames } from '../../Services/helpers'
import { useSession } from '../../Components/Session';
import Loading from '../Loading';
import { usePageContext } from '../Containers/Page';

const TheDropdown = ({options, disabled, className, icon, iconTheme, label, btnClassnames, flush, loading, openUp, openRight}) => {
    const {hasFeature} = useSession();
    const btnElement = useRef();
    const pageContext = usePageContext();

    const estimatedHeight = useMemo(() => {
        if (!options) {
            return 0;
        }

        return options.reduce((acc, o) => {
            if (o.div) {
                return acc + 5;
            }
            if (o.section) {
                return acc + 16;
            }
            return acc + 20;
        }, 0);
    }, [options])

    const openHints = useMemo(() => {
        if (!btnElement?.current || !pageContext) {
            return {openUp, openRight};
        }

        const {bottom, left} = btnElement.current.getBoundingClientRect();
        const offset = 8;

        const hintOpenUp = bottom + offset + estimatedHeight > pageContext.offsetHeight;
        const hintOpenRight = left < (pageContext.offsetWidth / 2);

        return {
            openUp: (openUp === true || openUp === true) ? openUp : hintOpenUp,
            openRight: (openRight === true || openRight === true) ? openRight : hintOpenRight,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openUp, openRight, estimatedHeight, btnElement?.current, pageContext?.scrollTop, pageContext?.offsetHeight])

    const [filteredOptions, featureDisabledOptions] = useMemo(() => {
        if (!options) {
            return [[], []];
        }

        const filtered = options.filter(o => !o.feature || hasFeature(o.feature));
        const featureDisabled = options.filter(o => o.feature && !hasFeature(o.feature));

        return [filtered, featureDisabled];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options])

    return <Menu as="div" ref={btnElement} className={classNames('relative inline-block text-left float-right', className)}>
        <div>
            <Menu.Button disabled={disabled || loading}
                className={classNames(
                    'inline-flex w-full justify-center gap-x-1.5 rounded-md px-3 py-2 text-sm font-semibold',
                    (className && !className.includes('fill-')) && 'fill-gray-900',
                    !flush && 'bg-gray-50 disabled:bg-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:text-gray-500 disabled:fill-gray-500',
                    btnClassnames,
                )}
            >
                <span className="sr-only">Open options</span>
                {loading ? <Loading inline center /> : <Icon icon={icon ?? 'Bars'} theme={iconTheme ?? null} />}
                {label && <span>{label}</span>}
            </Menu.Button>
        </div>

        <Transition
            as={Fragment}
            // enter="transition ease-out duration-100"
            // enterFrom="transform opacity-0 scale-95"
            // enterTo="transform opacity-100 scale-100"
            // leave="transition ease-in duration-75"
            // leaveFrom="transform opacity-100 scale-100"
            // leaveTo="transform opacity-0 scale-95"
        >
            <div className={classNames(
                'absolute z-30 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
                openHints.openUp ? 'bottom-0 mb-11' : '',
                openHints.openRight ? 'left-0' : 'right-0',
            )}>
                <Menu.Items>
                    <div className="py-1">
                        {filteredOptions.map((item, i) => <Item key={i} index={i} {...item} />)}
                        {featureDisabledOptions.length > 0 && <Item section="Not available on your plan" />}
                        {featureDisabledOptions.map((item, i) => <Item key={i} {...item} disabled={true} />)}
                    </div>
                </Menu.Items>
            </div>
        </Transition>
    </Menu>
}

export default TheDropdown
